import React, { useEffect, useState, useRef } from 'react'
import _ from 'lodash'
import {
  GoogleMap,
  DirectionsRenderer,
  useLoadScript,
  Rectangle,
  Circle,
  Polyline,
} from '@react-google-maps/api'
import PropTypes from 'prop-types'
import './style.scss'
import { GOOGLE_MAP_CONSTANTS } from '../../constants/googleMapConstants'
import { LANGUAGE } from '../../constants/appContants'
import MarkerWithEticket from './Markers/MarkerWithEticket'
import MarkerWithTitle from './Markers/MarkerWithTitle'
import CustomInfoWindow from './Markers/InfoWindow'
import GestureIcon from '@mui/icons-material/Gesture'
import CropSquareIcon from '@mui/icons-material/CropSquare'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ReplayIcon from '@mui/icons-material/Replay'
import IconButton from '@mui/material/IconButton'
import { decode } from '@mapbox/polyline'
import MarkerWithBorder from './Markers/MarkerWithBorder'
import { SELECTED_COURSE_ENUM } from '../../utils/values'
import DeselectIcon from '@mui/icons-material/Deselect'
import Tooltip from '@mui/material/Tooltip'
import { ajusterChevauchementsMarqueurs } from '../../utils/tours'

const librarries = ['geometry']

const GoogleMapComponent = ({
  setScriptLoadedSuccess,
  setScriptLoadedError,
  mapOptions,
  directions,
  markers,
  mapContainerStyle,
  mapId,
  isDrawable,
  polyLines,
  setUnselectTrigger,
  unselectTrigger,
  focusedTours,
  removeSelectedCourses,
  addSelectedCourses,
  selectedCourses,
  removeAllSelected,
}) => {
  const [showIndex, setShow] = useState(-1)
  const [enableDrawing, setEnableDrawing] = useState(false)
  const [drawingMode, setDrawingMode] = useState(null)
  const [overlays, setOverlays] = useState([])
  const mapRef = useRef(null)
  const drawingRef = useRef({
    overlay: null,
    points: [],
    isDrawing: false,
    startPoint: null,
  })
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    googleMapsClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    language: { LANGUAGE },
    libraries: librarries,
  })
  const isProgrammerPage = window.location.pathname.includes('programmer')
    ? true
    : false
  const colorArray = [
    '#2979ff',
    '#ff1744',
    '#76ff03',
    '#ff00ff',
    '#ff6d00',
    '#ffff00',
    '#00e5ff',
    '#ff5252',
    '#00c853',
    '#aa00ff',
    '#ff9100',
    '#ffd600',
    '#304ffe',
    '#d50000',
    '#b2ff59',
    '#e040fb',
    '#ffab40',
    '#ffff00',
    '#00b8d4',
    '#d500f9',
    '#6200ea',
  ]
  const [path, setPath] = useState([])
  //const [precision, setprecision] = useState(5)
  // Adds options as in showing points of interest removing popUp text on default markers etc ..
  const MapStyles = [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
  ]

  const onMapLoaded = (map) => {
    mapRef.current = map
    setScriptLoadedSuccess()
    if (polyLines?.length > 0) {
      const decoded = polyLines.map((poly) => {
        const int = decode(poly.path[0], 6).map((e) => {
          return { lat: e[0], lng: e[1] }
        })
        return { ...poly, path: int }
      })

      setPath(decoded)
    }
  }
  useEffect(() => {
    if (polyLines?.length >= 0) {
      const decoded = polyLines.map((poly) => {
        if (poly !== undefined && poly.path[0] !== null) {
          const int = decode(poly.path[0], 6).map((e) => {
            return { lat: e[0], lng: e[1] }
          })
          return { ...poly, path: int }
        }
      })
      setPath(decoded)
    }
  }, [polyLines])

  useEffect(() => {
    if (isDrawable && unselectTrigger) {
      setOverlays([])
      setUnselectTrigger(false)
    }
  }, [unselectTrigger])

  useEffect(() => {
    if (selectedCourses?.length == 0) {
      setOverlays([])
    }
  }, [selectedCourses])

  useEffect(() => {
    if (isDrawable) {
      //Get Points inside the geometric
      let bounds = []
      // let circleBounds = []
      overlays.map((e) => {
        if (e.type === 'rectangle' || e.type === 'circle')
          bounds.push(e.overlay.getBounds())
      })
      //Get if marker is selcted inside one of the geometric
      const selectedMarkers = markers
        .filter(({ isFocused }) => isFocused)
        .map((e) => {
          const markerLatLng = [e.position.lat, e.position.lng]

          if (bounds?.length > 0) {
            for (let i = 0; i < bounds.length; i++) {
              if (
                bounds[i]?.contains(
                  new window.google.maps.LatLng(markerLatLng[0], markerLatLng[1])
                )
              ) {
                return e.codeCourse
              }
            }
          }
        })

      //Filter the undefined options
      const filteredSelectedMarkers = selectedMarkers.filter((e) => e !== undefined)

      //TODO: USE SET FOR A-B
      const toAdd = filteredSelectedMarkers?.filter(
        (e) => !selectedCourses?.includes(e)
      )

      const toRemove = selectedCourses?.filter(
        (e) => !filteredSelectedMarkers?.includes(e)
      )
      if (toAdd?.length > 0) {
        addSelectedCourses({ data: toAdd, source: SELECTED_COURSE_ENUM.MAP })
      }
      if (toRemove?.length > 0) {
        removeSelectedCourses({ data: toRemove, source: SELECTED_COURSE_ENUM.MAP })
      }
    }
  }, [overlays])
  useEffect(() => {
    return () => {
      setScriptLoadedError()
    }
  }, [])
  const handleAddOnClick = (codeCourse) => {
    if (!selectedCourses.includes(codeCourse)) {
      addSelectedCourses({ data: [codeCourse], source: SELECTED_COURSE_ENUM.MAP })
    } else {
      removeSelectedCourses({ data: [codeCourse], source: SELECTED_COURSE_ENUM.MAP })
    }
  }
  const handleKeyPress = (event) => {
    // Check for specific key combination, e.g., Ctrl + S

    if (event.ctrlKey && event.key === 'z') {
      event.preventDefault()
      // Prevent the default browser action
      // Add your event logic here
      onUndoClick()
    }
    if (event.shiftKey) {
      event.preventDefault()
      setEnableDrawing(!enableDrawing)
      // Prevent the default browser action
      // Add your event logic here
    }
  }
  useEffect(() => {
    // attach the event listener
    document.addEventListener('keydown', handleKeyPress)

    // remove the event listener
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [handleKeyPress])

  const onEnableDrawing = () => {
    if (enableDrawing) {
      cancelNativeDrawing()
      setEnableDrawing(false)
      setDrawingMode(null)
    } else {
      setEnableDrawing(true)
      setDrawingMode('rectangle')
    }
  }

  const cancelNativeDrawing = () => {
    const state = drawingRef.current
    if (state.overlay) {
      state.overlay.setMap(null)
      state.overlay = null
    }
    state.points = []
    state.isDrawing = false
    state.startPoint = null
  }

  const changeDrawingMode = (mode) => {
    cancelNativeDrawing()
    setDrawingMode(mode)
  }

  const DRAWING_STYLE = {
    fillColor: '#199ee0',
    fillOpacity: 0.15,
    strokeWeight: 2,
    strokeColor: '#113460',
    clickable: false,
    editable: false,
    geodesic: false,
    zIndex: 100,
  }

  useEffect(() => {
    const map = mapRef.current
    if (!map || !enableDrawing || !drawingMode || !window.google) return

    const state = drawingRef.current
    const listeners = []

    map.setOptions({ draggable: false })
    const mapDiv = map.getDiv()
    if (mapDiv) mapDiv.style.cursor = 'crosshair'

    if (drawingMode === 'rectangle') {
      listeners.push(
        map.addListener('mousedown', (e) => {
          state.startPoint = e.latLng
          state.isDrawing = true
          if (!state.overlay) {
            state.overlay = new window.google.maps.Rectangle({
              bounds: new window.google.maps.LatLngBounds(
                state.startPoint,
                state.startPoint
              ),
              ...DRAWING_STYLE,
            })
            state.overlay.setMap(map)
          }
        }),
        map.addListener('mousemove', (e) => {
          if (!state.isDrawing || !state.overlay) return
          const bounds = new window.google.maps.LatLngBounds()
          bounds.extend(state.startPoint)
          bounds.extend(e.latLng)
          state.overlay.setBounds(bounds)
        }),
        map.addListener('mouseup', (e) => {
          if (!state.isDrawing) return
          state.isDrawing = false
          const bounds = new window.google.maps.LatLngBounds()
          bounds.extend(state.startPoint)
          bounds.extend(e.latLng)
          const overlay = new window.google.maps.Rectangle({
            bounds,
            ...DRAWING_STYLE,
          })
          overlay.setMap(null)
          setOverlays((prev) => [...prev, { overlay, type: 'rectangle' }])
          cancelNativeDrawing()
        })
      )
    } else if (drawingMode === 'circle') {
      listeners.push(
        map.addListener('mousedown', (e) => {
          state.startPoint = e.latLng
          state.isDrawing = true
          if (!state.overlay) {
            state.overlay = new window.google.maps.Circle({
              center: state.startPoint,
              radius: 1,
              ...DRAWING_STYLE,
            })
            state.overlay.setMap(map)
          }
        }),
        map.addListener('mousemove', (e) => {
          if (!state.isDrawing || !state.overlay) return
          const radius =
            window.google.maps.geometry.spherical.computeDistanceBetween(
              state.startPoint,
              e.latLng
            )
          state.overlay.setRadius(radius)
        }),
        map.addListener('mouseup', (e) => {
          if (!state.isDrawing) return
          state.isDrawing = false
          const radius =
            window.google.maps.geometry.spherical.computeDistanceBetween(
              state.startPoint,
              e.latLng
            )
          const overlay = new window.google.maps.Circle({
            center: state.startPoint,
            radius,
            ...DRAWING_STYLE,
          })
          overlay.setMap(null)
          setOverlays((prev) => [...prev, { overlay, type: 'circle' }])
          cancelNativeDrawing()
        })
      )
    }

    return () => {
      listeners.forEach((l) => window.google.maps.event.removeListener(l))
      cancelNativeDrawing()
      map.setOptions({ draggable: true })
      if (mapDiv) mapDiv.style.cursor = ''
    }
  }, [enableDrawing, drawingMode])

  // const handleRemoveCourses = () => {
  //   const a = previousSelectedMarkers.pop()
  //   console.log(a, 'toRem')
  //   console.log('toRe', selectedCourses)
  //   // this should be changedI
  //   const toRemove = selectedCourses.filter((e) => !a.includes(e))
  //   console.log('toRemove', toRemove)
  //   removeSelectedCourses({
  //     data: toRemove,
  //     source: SELECTED_COURSE_ENUM.MAP,
  //   })
  // }

  const onUndoClick = () => {
    setOverlays([...overlays].slice(0, -1))
    // handleRemoveCourses()
  }

  const renderDrawingModeButton = (mode, Icon, label) => (
    <IconButton
      sx={{
        padding: '7px',
        backgroundColor: drawingMode === mode ? '#e3f2fd' : 'rgb(255 255 255)',
        borderRadius: 'unset',
        borderLeft:
          drawingMode === mode ? '3px solid #1976d2' : '3px solid transparent',
        ':hover': {
          color: 'rgb(51 51 51)',
          backgroundColor: 'rgb(255 255 255)',
        },
      }}
      aria-label={label}
      component="span"
      onClick={() => changeDrawingMode(mode)}
    >
      <Icon
        style={{
          fontSize: '1.5rem',
          color: drawingMode === mode ? '#1976d2' : undefined,
        }}
      />
    </IconButton>
  )

  const RenderDrawControls = () => {
    return (
      <div className="draw-controls">
        {selectedCourses.length > 0 && (
          <Tooltip title="Tout déselectionner" placement="bottom">
            <IconButton
              sx={{
                padding: '7px',
                backgroundColor: 'rgb(255 255 255) ',
                borderRadius: 'unset',
                ':hover': {
                  color: 'rgb(51 51 51)',
                  backgroundColor: 'rgb(255 255 255)',
                },
              }}
              component="span"
              onClick={removeAllSelected}
            >
              <DeselectIcon style={{ fontSize: '2rem' }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Annuler le dernier dessin" placement="bottom">
          <IconButton
            sx={{
              padding: '7px',
              backgroundColor: 'rgb(255 255 255)',
              borderRadius: 'unset',
              ':hover': {
                color: 'rgb(51 51 51)',
                backgroundColor: 'rgb(255 255 255)',
              },
            }}
            component="span"
            onClick={onUndoClick}
          >
            <ReplayIcon style={{ fontSize: '2rem' }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={enableDrawing ? 'Désactiver le dessin' : 'Activer le dessin'}
          placement="bottom"
        >
          <IconButton
            sx={{
              padding: '7px',
              backgroundColor: enableDrawing ? '#e3f2fd' : 'rgb(255 255 255)',
              borderRadius: 'unset',
              ':hover': {
                color: 'rgb(51 51 51)',
                backgroundColor: 'rgb(255 255 255)',
              },
            }}
            component="span"
            onClick={onEnableDrawing}
          >
            <GestureIcon
              style={{
                fontSize: '2rem',
                color: enableDrawing ? '#1976d2' : undefined,
              }}
            />
          </IconButton>
        </Tooltip>
        {enableDrawing && (
          <div className="draw-mode-selector">
            {renderDrawingModeButton('rectangle', CropSquareIcon, 'Rectangle')}
            {renderDrawingModeButton('circle', RadioButtonUncheckedIcon, 'Cercle')}
          </div>
        )}
      </div>
    )
  }

  const renderOverlay = () => {
    const options = {
      fillColor: '#199ee0',
      fillOpacity: 0.2,
      strokeWeight: 2,
      strokeColor: '#113460',
      clickable: true,
      editable: true,
      geodesic: false,
      visible: true,
      zIndex: 1,
    }
    return overlays.map((overlay, i) => {
      switch (overlay.type) {
        case 'rectangle':
          return (
            <Rectangle
              bounds={overlay.overlay.getBounds()}
              key={i}
              options={options}
            />
          )
        case 'circle':
          return (
            <Circle
              center={overlay.overlay.getCenter()}
              radius={overlay.overlay.getRadius()}
              key={i}
              options={options}
            />
          )
        default:
          return null
      }
    })
  }
  const renderMap = () => {
    return (
      <>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          id={mapId}
          zoom={mapOptions.zoom}
          center={mapOptions.center}
          onLoad={onMapLoaded}
          options={{
            ..._.get(mapOptions, 'center', GOOGLE_MAP_CONSTANTS.MAP_OPTIONS),
            ...GOOGLE_MAP_CONSTANTS.DEFAULT_UI_OPTIONS,
            styles: MapStyles,
          }}
        >
          {directions &&
            directions.length &&
            directions.map((d, index) => (
              <DirectionsRenderer
                key={`direction_${index}`}
                // required
                directions={d.directions}
                // optional
                onLoad={(directionsRenderer) => {
                  console.log(
                    'DirectionsRenderer onLoad directionsRenderer: ',
                    directionsRenderer
                  )
                }}
                // optional
                // onUnmount={(directionsRenderer) => {
                //   console.log(
                //     'DirectionsRenderer onUnmount directionsRenderer: ',
                //     directionsRenderer
                //   )
                // }}
                routeIndex={d.selectedDirection ? d.selectedDirection.index : 0}
              />
            ))}
          {window.google &&
            path.map((e, i) => {
              return (
                <Polyline
                  key={i}
                  path={e.path}
                  options={{
                    strokeColor:
                      e?.color != '#000000'
                        ? e.color
                        : colorArray[i % colorArray.length],
                    strokeOpacity:
                      focusedTours && focusedTours.length > 0
                        ? focusedTours.includes(e.index)
                          ? 1
                          : 0
                        : 0,
                    strokeWeight: 5,
                  }}
                />
              )
            })}

          {markers &&
            window.google &&
            markers.length &&
            ajusterChevauchementsMarqueurs(markers)?.map((m, i) => (
              <div key={`marker_info_${i}`}>
                {m.color ? (
                  (focusedTours.includes(m.index) ||
                    m.ticket === 'R' ||
                    m.ticket === 'L') && (
                    <MarkerWithBorder
                      marker={m}
                      showIndex={showIndex}
                      index={i}
                      setShow={setShow}
                      strokeColor={m.color}
                      onSelectMark={handleAddOnClick}
                    />
                  )
                ) : m.ticket || isDrawable ? (
                  <MarkerWithEticket
                    disableDraw={isProgrammerPage}
                    marker={m}
                    showIndex={showIndex}
                    index={i}
                    setShow={setShow}
                    isDrawable={isDrawable}
                  />
                ) : (
                  <MarkerWithTitle marker={m} index={i} />
                )}
                {showIndex === i && (
                  <CustomInfoWindow
                    content={m.ticket || isDrawable ? m.infoText : m.title}
                    index={i}
                    position={m.position}
                  />
                )}
              </div>
            ))}
          {isDrawable && <RenderDrawControls />}
          {isDrawable && !unselectTrigger && overlays.length > 0 && renderOverlay()}
        </GoogleMap>
      </>
    )
  }

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>
  }

  return isLoaded ? renderMap() : <div>Loading...</div>
}

GoogleMapComponent.propTypes = {
  setScriptLoadedSuccess: PropTypes.func.isRequired,
  setScriptLoadedError: PropTypes.func.isRequired,
  directions: PropTypes.array,
  markers: PropTypes.array,
  mapOptions: PropTypes.object.isRequired,
  isDrawable: PropTypes.bool,
  polyLines: PropTypes.any,
  setUnselectTrigger: PropTypes.func,
  unselectTrigger: PropTypes.bool,
  focusedTours: PropTypes.array,
  addSelectedCourses: PropTypes.func,
  removeSelectedCourses: PropTypes.func,
  selectedCourses: PropTypes.array,
  /** Used for adding style to the container */
  mapContainerStyle: PropTypes.any,
  /** the className of the mapContainer */
  mapId: PropTypes.any,
  removeAllSelected: PropTypes.func,
}
export default GoogleMapComponent
